import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  Edit3,
  Trash2,
  Search,
  Warehouse,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import StorageLocationFormModal from "./StorageLocationFormModal";
import storageLocationService from "@/services/storageLocationService";
import { showError, showInfo } from "@/store/errorStore";

/**
 * Componente de gerenciamento de locais de armazenamento
 * CRUD completo com listagem, criação, edição e exclusão
 */
export default function StorageLocationsManagement() {
  const [storageLocations, setStorageLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [savingLocation, setSavingLocation] = useState(false);
  const [deletingLocationId, setDeletingLocationId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  /**
   * Carregar locais de armazenamento
   */
  const loadData = async () => {
    setLoading(true);
    try {
      const data = await storageLocationService.getAllStorageLocations();
      setStorageLocations(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(
        error.response?.data?.message || "Erro ao carregar dados",
        "Erro de Carregamento"
      );
      setStorageLocations([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Buscar todos os locais
   */
  const fetchStorageLocations = async () => {
    try {
      const data = await storageLocationService.getAllStorageLocations();
      setStorageLocations(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(
        error.response?.data?.message ||
          "Erro ao carregar locais de armazenamento",
        "Erro de Carregamento"
      );
      setStorageLocations([]);
    }
  };

  /**
   * Abrir modal para criar novo local
   */
  const handleCreateLocation = () => {
    setEditingLocation(null);
    setShowModal(true);
  };

  /**
   * Abrir modal para editar local
   */
  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setShowModal(true);
  };

  /**
   * Salvar local (criar ou atualizar)
   */
  const handleSaveLocation = async (locationData) => {
    setSavingLocation(true);
    try {
      if (editingLocation) {
        // Atualizar local existente
        await storageLocationService.updateStorageLocation(
          editingLocation.id,
          locationData
        );
        showInfo(
          "Local de armazenamento atualizado com sucesso",
          "Local Atualizado"
        );
      } else {
        // Criar novo local
        await storageLocationService.createStorageLocation(locationData);
        showInfo("Local de armazenamento criado com sucesso", "Local Criado");
      }

      setShowModal(false);
      setEditingLocation(null);
      await fetchStorageLocations();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Erro ao salvar local de armazenamento";
      showError(errorMessage, "Erro");
    } finally {
      setSavingLocation(false);
    }
  };

  /**
   * Confirmar exclusão de local
   */
  const handleDeleteClick = (location) => {
    setDeletingLocationId(location.id);
    setShowDeleteDialog(true);
  };

  /**
   * Executar exclusão de local
   */
  const handleConfirmDelete = async () => {
    if (!deletingLocationId) return;

    try {
      await storageLocationService.deleteStorageLocation(deletingLocationId);
      showInfo("Local de armazenamento removido com sucesso", "Local Deletado");
      await fetchStorageLocations();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erro ao deletar local de armazenamento";
      showError(errorMessage, "Erro");
    } finally {
      setShowDeleteDialog(false);
      setDeletingLocationId(null);
    }
  };

  /**
   * Filtrar locais por busca
   */
  const filteredLocations = storageLocations.filter((location) => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    const name = (location?.name || "").toLowerCase();
    const typeLabel = location?.type
      ? storageLocationService.getStorageLocationTypeLabel(location.type)
      : "";
    const type = typeLabel.toLowerCase();

    return name.includes(query) || type.includes(query);
  });

  /**
   * Calcular estatísticas
   */
  const calculateStats = () => {
    const totalCapacity = storageLocations.reduce(
      (sum, loc) => sum + (loc.capacity || 0),
      0
    );
    const totalUsed = storageLocations.reduce(
      (sum, loc) => sum + (loc.used_capacity || 0),
      0
    );
    const totalAvailable = totalCapacity - totalUsed;
    const usagePercentage =
      totalCapacity > 0 ? Math.round((totalUsed / totalCapacity) * 100) : 0;

    const criticalLocations = storageLocations.filter((loc) => {
      const usedCapacity = loc.used_capacity || 0;
      const usage = storageLocationService.calculateUsagePercentage(
        loc.capacity,
        usedCapacity
      );
      return usage >= 90;
    }).length;

    return {
      totalLocations: storageLocations.length,
      totalCapacity,
      totalUsed,
      totalAvailable,
      usagePercentage,
      criticalLocations,
    };
  };

  const stats = calculateStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Locais de Armazenamento
          </h1>
          <p className="text-gray-500 mt-1">
            {filteredLocations.length}{" "}
            {filteredLocations.length === 1 ? "local" : "locais"} cadastrados
          </p>
        </div>

        <Button
          onClick={handleCreateLocation}
          style={{ background: "#c9332C" }}
          onMouseEnter={(e) => (e.target.style.background = "#a02820")}
          onMouseLeave={(e) => (e.target.style.background = "#c9332C")}
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Local
        </Button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">
              Total de Locais
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalLocations}</div>
            <p className="text-xs text-gray-500 mt-1">Locais cadastrados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">
              Capacidade Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalCapacity}</div>
            <p className="text-xs text-gray-500 mt-1">Unidades disponíveis</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">
              Taxa de Ocupação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.usagePercentage}%</div>
            <Progress value={stats.usagePercentage} className="mt-2" />
            <p className="text-xs text-gray-500 mt-1">
              {stats.totalUsed} de {stats.totalCapacity} usados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">
              Locais Críticos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                stats.criticalLocations > 0 ? "text-red-600" : "text-green-600"
              }`}
            >
              {stats.criticalLocations}
            </div>
            <p className="text-xs text-gray-500 mt-1">≥ 90% de ocupação</p>
          </CardContent>
        </Card>
      </div>

      {/* Busca */}
      <Card>
        <CardHeader>
          <CardTitle>Buscar Locais</CardTitle>
          <CardDescription>Pesquise por nome ou tipo de local</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Digite para buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Locais */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Locais de Armazenamento</CardTitle>
          <CardDescription>
            Gerencie os locais de armazenamento do sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando locais...</p>
              </div>
            </div>
          ) : filteredLocations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Warehouse className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-gray-600 font-medium">
                {searchQuery
                  ? "Nenhum local encontrado"
                  : "Nenhum local cadastrado"}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {searchQuery
                  ? "Tente outro termo de busca"
                  : "Clique em 'Novo Local' para começar"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Equipamento</TableHead>
                    <TableHead>Capacidade</TableHead>
                    <TableHead>Disponível</TableHead>
                    <TableHead>Ocupação</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLocations.map((location) => {
                    const usedCapacity = location.used_capacity || 0;
                    const available = location.capacity - usedCapacity;
                    const usagePercentage =
                      storageLocationService.calculateUsagePercentage(
                        location.capacity,
                        usedCapacity
                      );
                    const statusColor =
                      storageLocationService.getUsageStatusColor(
                        usagePercentage
                      );
                    const statusLabel =
                      storageLocationService.getUsageStatusLabel(
                        usagePercentage
                      );

                    return (
                      <TableRow key={location.id}>
                        <TableCell className="font-mono text-sm text-gray-500">
                          #{location.id}
                        </TableCell>
                        <TableCell className="font-medium">
                          {location.name}
                        </TableCell>
                        <TableCell>{location.equipment_name || "-"}</TableCell>
                        <TableCell className="font-semibold">
                          {location.capacity}
                        </TableCell>
                        <TableCell className="font-semibold text-green-600">
                          {available}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">
                                {usagePercentage}%
                              </span>
                            </div>
                            <Progress value={usagePercentage} className="h-2" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`
                              ${
                                statusColor === "green"
                                  ? "bg-green-100 text-green-800 border-green-200"
                                  : ""
                              }
                              ${
                                statusColor === "yellow"
                                  ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                                  : ""
                              }
                              ${
                                statusColor === "red"
                                  ? "bg-red-100 text-red-800 border-red-200"
                                  : ""
                              }
                            `}
                          >
                            {statusColor === "green" && (
                              <CheckCircle className="w-3 h-3 mr-1" />
                            )}
                            {statusColor === "yellow" && (
                              <AlertTriangle className="w-3 h-3 mr-1" />
                            )}
                            {statusColor === "red" && (
                              <AlertTriangle className="w-3 h-3 mr-1" />
                            )}
                            {statusLabel}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditLocation(location)}
                            >
                              <Edit3 className="w-4 h-4 mr-1" />
                              Editar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteClick(location)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Excluir
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Storage Location Form Modal */}
      <StorageLocationFormModal
        open={showModal}
        onOpenChange={setShowModal}
        storageLocation={editingLocation}
        onSave={handleSaveLocation}
        loading={savingLocation}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este local de armazenamento? Esta
              ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingLocationId(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
